import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  deleteAdminAccessory,
  getAdminAccessories,
  getAdminAccessoryById,
  removeAdminAccessoryImage,
  saveAdminAccessoryAllLanguages,
  uploadAdminAccessoryImage,
} from "../../../api/metalsAdmin";
import { useNotification } from "../../../context/NotificationContext";
import AdminProductPdfsPanel from "./AdminProductPdfsPanel";
import AdminEditorModal from "./AdminEditorModal";
import AdminAddColumnModal from "./AdminAddColumnModal";
import AdminCollapsibleSection from "./AdminCollapsibleSection";
import AdminProductListCard from "./AdminProductListCard";
import { btnAccent, btnDanger, btnPrimary, btnSecondary, langTabActive, langTabIdle } from "./adminProductUi";
import { resolveMediaUrl } from "../../../utils/media";
import { DashboardReloadButton } from "./dashboardUi";
import { normalizeKey, toHeaderLabel } from "../../../utils/metalFrameDimensionsTable";

const LANGS = ["en", "ar", "ku"];
const LANG_LABELS = { en: "English", ar: "Arabic", ku: "Kurdish" };

const ACCESSORY_DEFAULT_COLUMNS = [
  { key: "role", label: "Role" },
  { key: "h1", label: "H1" },
  { key: "width", label: "Width" },
  { key: "thickness", label: "Thickness" },
  { key: "length", label: "Length" },
  { key: "zincCoating", label: "Zinc Coating" },
  { key: "type", label: "Type" },
  { key: "density", label: "Density" },
  { key: "standards", label: "Standards" },
];

const normLang = (value) =>
  String(value || "en")
    .toLowerCase()
    .split("-")[0];

const normKey = (value) => String(value || "").trim().toLowerCase();

const makeAccessoryEmptyRow = (cols) => ({
  values: cols.reduce((acc, col) => {
    acc[col.key] = "";
    return acc;
  }, {}),
});

/** "Fire Rating" → "fireRating", "H2" → "h2" — camelCase key for a new column. */
const labelToColumnKey = (label) => {
  const parts = String(label || "").split(/[^a-zA-Z0-9]+/).filter(Boolean);
  if (!parts.length) return "";
  return parts
    .map((p, i) => (i === 0 ? p.toLowerCase() : p[0].toUpperCase() + p.slice(1).toLowerCase()))
    .join("");
};

/** API columns + columns for any extra row-value keys with data, so nothing is ever hidden. */
const mergeColumnsWithRowKeys = (cols, rawRows) => {
  const known = new Set(cols.map((c) => normalizeKey(c.key)));
  const merged = [...cols];
  rawRows.forEach((row) => {
    Object.entries(row?.values || {}).forEach(([key, value]) => {
      const normalized = normalizeKey(key);
      if (!normalized || known.has(normalized) || !String(value ?? "").trim()) return;
      known.add(normalized);
      merged.push({ key, label: toHeaderLabel(key) });
    });
  });
  return merged;
};

/** All translatable content for a single language variant of an accessory. */
const emptyLangFields = () => ({
  name: "",
  heroTag: "ACCESSORIES",
  heroTitle: "",
  heroDescription: "",
  heroDescriptionMore: "",
  heroBullets: [{ text: "" }],
  standards: [{ text: "" }],
  materialProperties: [{ label: "", value: "" }],
  sectionDimensions: [{ label: "", value: "" }],
  dimensionsTableColumns: ACCESSORY_DEFAULT_COLUMNS,
  dimensionsTableRows: [makeAccessoryEmptyRow(ACCESSORY_DEFAULT_COLUMNS)],
});

const emptyTranslations = () => ({
  en: emptyLangFields(),
  ar: emptyLangFields(),
  ku: emptyLangFields(),
});

const EMPTY_FORM = {
  accessoryKey: "",
  imageUrl: "",
  diagramUrl: "",
  isActive: true,
  isCrossSection: false,
  translations: emptyTranslations(),
  ids: {},
  primaryId: null,
};

/** Map a single DB accessory row into the per-language form field shape. */
const mapRowToLangFields = (row) => {
  const apiCols =
    Array.isArray(row?.dimensionsTableColumns) && row.dimensionsTableColumns.length > 0
      ? [...row.dimensionsTableColumns]
          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
          .map((c) => ({ key: String(c.key || ""), label: String(c.label || c.key || "") }))
      : ACCESSORY_DEFAULT_COLUMNS;

  const rawRows =
    Array.isArray(row?.dimensionsTable) && row.dimensionsTable.length > 0
      ? [...row.dimensionsTable].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
      : [];

  const cols = mergeColumnsWithRowKeys(apiCols, rawRows);

  const dimensionsTableRows =
    rawRows.length > 0
      ? rawRows.map((r) => ({
          values: cols.reduce((acc, col) => {
            acc[col.key] = String(r.values?.[col.key] ?? r[col.key] ?? "");
            return acc;
          }, {}),
        }))
      : [makeAccessoryEmptyRow(cols)];

  return {
    name: row?.name || "",
    heroTag: row?.heroTag || "ACCESSORIES",
    heroTitle: row?.heroTitle || "",
    heroDescription: row?.heroDescription || "",
    heroDescriptionMore: row?.heroDescriptionMore || "",
    heroBullets:
      Array.isArray(row?.heroBullets) && row.heroBullets.length
        ? row.heroBullets.map((x) => ({ text: x?.text || "" }))
        : [{ text: "" }],
    standards:
      Array.isArray(row?.standards) && row.standards.length
        ? row.standards.map((x) => ({ text: x?.text || "" }))
        : [{ text: "" }],
    materialProperties:
      Array.isArray(row?.materialProperties) && row.materialProperties.length
        ? row.materialProperties.map((x) => ({ label: x?.label || "", value: x?.value || "" }))
        : [{ label: "", value: "" }],
    sectionDimensions:
      Array.isArray(row?.sectionDimensions) && row.sectionDimensions.length
        ? row.sectionDimensions.map((x) => ({ label: x?.label || "", value: x?.value || "" }))
        : [{ label: "", value: "" }],
    dimensionsTableColumns: cols,
    dimensionsTableRows,
  };
};

/** Build one form (all languages) from the sibling rows of one accessory key. */
const mapAccessoriesToForm = (rows) => {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  const primary = list.find((r) => normLang(r?.language) === "en") || list[0] || {};
  const translations = emptyTranslations();
  const ids = {};
  list.forEach((row) => {
    const lang = normLang(row?.language);
    if (!LANGS.includes(lang)) return;
    translations[lang] = mapRowToLangFields(row);
    ids[lang] = row?.id ?? null;
  });
  return {
    accessoryKey: primary?.accessoryKey || "",
    imageUrl: primary?.imageUrl || list.find((r) => r?.imageUrl)?.imageUrl || "",
    diagramUrl: primary?.diagramUrl || list.find((r) => r?.diagramUrl)?.diagramUrl || "",
    isActive: list.length ? list.every((r) => r?.isActive !== false) : true,
    isCrossSection: Boolean(primary?.isCrossSection),
    translations,
    ids,
    primaryId: primary?.id ?? list[0]?.id ?? null,
  };
};

const langHasContent = (f) =>
  Boolean((f?.name || "").trim() && (f?.heroTitle || "").trim() && (f?.heroDescription || "").trim());

/** Convert one language's form fields into a backend language-content payload. */
const toLanguageContent = (lang, fields) => ({
  language: lang,
  name: (fields.name || "").trim(),
  heroTag: (fields.heroTag || "ACCESSORIES").trim() || "ACCESSORIES",
  heroTitle: (fields.heroTitle || "").trim(),
  heroDescription: (fields.heroDescription || "").trim(),
  heroDescriptionMore: (fields.heroDescriptionMore || "").trim() || null,
  heroBullets: (fields.heroBullets || [])
    .map((x) => ({ text: (x?.text || "").trim() }))
    .filter((x) => x.text),
  standards: (fields.standards || [])
    .map((x) => ({ text: (x?.text || "").trim() }))
    .filter((x) => x.text),
  materialProperties: (fields.materialProperties || [])
    .map((x) => ({ label: (x?.label || "").trim(), value: (x?.value || "").trim() }))
    .filter((x) => x.label),
  sectionDimensions: (fields.sectionDimensions || [])
    .map((x) => ({ label: (x?.label || "").trim(), value: (x?.value || "").trim() }))
    .filter((x) => x.label && x.value),
  dimensionsTableColumns: (fields.dimensionsTableColumns || ACCESSORY_DEFAULT_COLUMNS)
    .map((col) => ({ key: (col.key || "").trim(), label: (col.label || "").trim() }))
    .filter((col) => col.key && col.label),
  dimensionsTableRows: (fields.dimensionsTableRows || [])
    .filter((row) => Object.values(row?.values || {}).some((v) => String(v || "").trim()))
    .map((row) => ({ values: { ...(row?.values || {}) } })),
});

/** Build the multi-language save payload. Incomplete languages are omitted. */
const buildSavePayload = (form) => ({
  accessoryKey: (form.accessoryKey || "").trim(),
  imageUrl: (form.imageUrl || "").trim() || null,
  diagramUrl: (form.diagramUrl || "").trim() || null,
  isActive: Boolean(form.isActive),
  isCrossSection: Boolean(form.isCrossSection),
  languages: LANGS.filter((lang) => langHasContent(form.translations?.[lang])).map((lang) =>
    toLanguageContent(lang, form.translations[lang])
  ),
});

const isSpaceKey = (event) => event.key === " " || event.code === "Space" || event.key === "Spacebar";

const isEditableTarget = (target) => {
  const tagName = String(target?.tagName || "").toUpperCase();
  const inputType = String(target?.type || "").toLowerCase();
  return (
    tagName === "TEXTAREA" ||
    (tagName === "INPUT" &&
      !["checkbox", "radio", "button", "submit", "reset", "file"].includes(inputType))
  );
};

const insertSpaceAtCursor = (target) => {
  if (!target || typeof target.value !== "string") return;
  const start = typeof target.selectionStart === "number" ? target.selectionStart : target.value.length;
  const end = typeof target.selectionEnd === "number" ? target.selectionEnd : target.value.length;
  const nextValue = `${target.value.slice(0, start)} ${target.value.slice(end)}`;
  const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    "value"
  )?.set;
  const nativeTextAreaValueSetter = Object.getOwnPropertyDescriptor(
    window.HTMLTextAreaElement.prototype,
    "value"
  )?.set;

  if (target.tagName === "TEXTAREA" && nativeTextAreaValueSetter) {
    nativeTextAreaValueSetter.call(target, nextValue);
  } else if (target.tagName === "INPUT" && nativeInputValueSetter) {
    nativeInputValueSetter.call(target, nextValue);
  } else {
    target.value = nextValue;
  }

  const cursor = start + 1;
  if (typeof target.setSelectionRange === "function") {
    target.setSelectionRange(cursor, cursor);
  }
  target.dispatchEvent(new Event("input", { bubbles: true }));
};

const stopSpaceScrollInsideEditor = (event) => {
  if (!isSpaceKey(event)) return;
  const target = event?.target;
  event.preventDefault();
  event.stopPropagation();
  if (isEditableTarget(target)) {
    insertSpaceAtCursor(target);
  }
};

const MetalsManager = () => {
  const { notify } = useNotification();
  const rootRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editingKey, setEditingKey] = useState(null);
  const [activeCreateLang, setActiveCreateLang] = useState("en");
  const [activeEditLang, setActiveEditLang] = useState("en");
  const [createImageFile, setCreateImageFile] = useState(null);
  const [editImageFile, setEditImageFile] = useState(null);
  /** Which form + language the "Add column" dialog targets: { formId, lang } | null. */
  const [addColumnFor, setAddColumnFor] = useState(null);

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await getAdminAccessories();
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      notify({ type: "error", title: "Failed to load accessories", message: error?.response?.data?.message || error?.message || "" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  useEffect(() => {
    const onNativeKeyDown = (event) => {
      if (!isSpaceKey(event)) return;
      const root = rootRef.current;
      if (!root || !root.contains(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
      if (isEditableTarget(event.target)) {
        insertSpaceAtCursor(event.target);
      }
    };

    window.addEventListener("keydown", onNativeKeyDown, { capture: true });
    return () => {
      window.removeEventListener("keydown", onNativeKeyDown, { capture: true });
    };
  }, []);

  // One card per accessory key; each card bundles all language variants.
  const groupedItems = useMemo(() => {
    const groups = new Map();
    items.forEach((item) => {
      const key = normKey(item?.accessoryKey) || `#${item?.id}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    });
    return [...groups.values()]
      .map((rows) => {
        const rep = rows.find((r) => normLang(r?.language) === "en") || rows[0];
        return {
          key: normKey(rep?.accessoryKey) || `#${rep?.id}`,
          rep,
          rows,
          languages: LANGS.filter((lang) => rows.some((r) => normLang(r?.language) === lang)),
        };
      })
      .sort((a, b) => Number(a?.rep?.id || 0) - Number(b?.rep?.id || 0));
  }, [items]);

  const refreshAccessoryKey = async (accessoryKey) => {
    await loadItems();
    if (accessoryKey) {
      const siblings = (await getAdminAccessories().catch(() => [])).filter(
        (b) => normKey(b?.accessoryKey) === normKey(accessoryKey)
      );
      const fresh = await Promise.all(siblings.map((s) => getAdminAccessoryById(s.id).catch(() => s)));
      mergeFreshIntoItems(fresh);
      setEditForm(mapAccessoriesToForm(fresh));
    }
  };

  const mergeFreshIntoItems = (fresh) =>
    setItems((prev) =>
      prev.map((x) => fresh.find((f) => Number(f?.id) === Number(x.id)) || x)
    );

  const handleAddColumn = (label) => {
    const key = labelToColumnKey(label);
    if (!key || !addColumnFor) return;
    const { formId, lang } = addColumnFor;
    const form = formId === "edit" ? editForm : createForm;
    const setForm = formId === "edit" ? setEditForm : setCreateForm;
    const cols = form.translations?.[lang]?.dimensionsTableColumns || ACCESSORY_DEFAULT_COLUMNS;
    if (cols.some((c) => normalizeKey(c.key) === normalizeKey(key))) {
      notify({ type: "error", title: "Column already exists", message: `A column "${label}" is already in the table.` });
      return;
    }
    setForm((prev) => {
      const t = prev.translations[lang];
      const current = t.dimensionsTableColumns || ACCESSORY_DEFAULT_COLUMNS;
      const nextCols = [...current, { key, label }];
      const nextRows = (t.dimensionsTableRows || []).map((r) => ({
        ...r,
        values: { ...(r?.values || {}), [key]: "" },
      }));
      return {
        ...prev,
        translations: {
          ...prev.translations,
          [lang]: { ...t, dimensionsTableColumns: nextCols, dimensionsTableRows: nextRows },
        },
      };
    });
    setAddColumnFor(null);
  };

  const handleCreate = async () => {
    const anyLang = LANGS.some((lang) => langHasContent(createForm.translations[lang]));
    if (!createForm.accessoryKey.trim() || !anyLang) {
      notify({ type: "error", title: "Accessory key and at least one complete language (Name, Hero Title, Hero Description) are required" });
      return;
    }
    setCreating(true);
    try {
      const payload = buildSavePayload(createForm);
      const savedRows = await saveAdminAccessoryAllLanguages(payload);
      if (createImageFile && Array.isArray(savedRows) && savedRows.length) {
        const rep = savedRows.find((b) => normLang(b?.language) === "en") || savedRows[0];
        if (rep?.id) {
          await uploadAdminAccessoryImage({ id: rep.id, file: createImageFile });
        }
      }
      notify({ type: "success", title: "Accessory saved for all languages" });
      setCreateForm(EMPTY_FORM);
      setCreateImageFile(null);
      setActiveCreateLang("en");
      await loadItems();
    } catch (error) {
      notify({ type: "error", title: "Save failed", message: error?.response?.data?.message || error?.message || "" });
    } finally {
      setCreating(false);
    }
  };

  const startEdit = async (group) => {
    const key = normKey(group?.rep?.accessoryKey) || group.key;
    setEditingKey(key);
    setEditImageFile(null);
    setActiveEditLang("en");
    setEditForm(mapAccessoriesToForm(group.rows));
    try {
      const fresh = await Promise.all(group.rows.map((row) => getAdminAccessoryById(row.id).catch(() => row)));
      mergeFreshIntoItems(fresh);
      setEditForm(mapAccessoriesToForm(fresh));
    } catch {
      /* keep list rows */
    }
  };

  const saveEdit = async () => {
    if (!editingKey) return;
    const anyLang = LANGS.some((lang) => langHasContent(editForm.translations[lang]));
    if (!editForm.accessoryKey.trim() || !anyLang) {
      notify({ type: "error", title: "Accessory key and at least one complete language are required" });
      return;
    }
    setSaving(true);
    try {
      const payload = buildSavePayload(editForm);
      const savedRows = await saveAdminAccessoryAllLanguages(payload);
      if (editImageFile) {
        const rep =
          (Array.isArray(savedRows) && (savedRows.find((b) => normLang(b?.language) === "en") || savedRows[0])) || null;
        if (rep?.id) {
          await uploadAdminAccessoryImage({ id: rep.id, file: editImageFile });
        }
      }
      notify({ type: "success", title: "Accessory updated for all languages" });
      setEditingKey(null);
      setEditForm(EMPTY_FORM);
      setEditImageFile(null);
      await loadItems();
    } catch (error) {
      notify({ type: "error", title: "Update failed", message: error?.response?.data?.message || error?.message || "" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (group) => {
    if (!window.confirm(`Delete accessory "${group?.rep?.name || group?.rep?.accessoryKey}" and all its language versions?`)) return;
    try {
      for (const row of group.rows) {
        // eslint-disable-next-line no-await-in-loop
        await deleteAdminAccessory({ id: row.id });
      }
      notify({ type: "success", title: "Accessory deleted" });
      await loadItems();
    } catch (error) {
      notify({ type: "error", title: "Delete failed", message: error?.response?.data?.message || error?.message || "" });
    }
  };

  const closeEdit = () => {
    setEditingKey(null);
    setEditForm(EMPTY_FORM);
    setEditImageFile(null);
    setActiveEditLang("en");
  };

  const editRepId = editForm.ids?.en ?? editForm.primaryId ?? null;

  const renderPairRows = (rows, setRowsDirect, labels = ["Label", "Value"]) => (
    <>
      {rows.map((row, idx) => (
        <div key={idx} className="grid sm:grid-cols-12 gap-2 mb-2">
          <input
            type="text"
            value={row?.label || ""}
            onChange={(e) => {
              const next = [...rows];
              next[idx] = { ...(next[idx] || {}), label: e.target.value };
              setRowsDirect(next);
            }}
            placeholder={labels[0]}
            className="sm:col-span-5 rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
          <input
            type="text"
            value={row?.value || ""}
            onChange={(e) => {
              const next = [...rows];
              next[idx] = { ...(next[idx] || {}), value: e.target.value };
              setRowsDirect(next);
            }}
            placeholder={labels[1]}
            className="sm:col-span-6 rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />
          <button
            type="button"
            onClick={() => {
              const next = rows.filter((_, i) => i !== idx);
              setRowsDirect(next.length ? next : [{ label: "", value: "" }]);
            }}
            className="px-3 rounded border border-rose-200 text-rose-700"
          >
            -
          </button>
        </div>
      ))}
    </>
  );

  // Shared per-language content editor used by both create and edit forms.
  const renderLangContent = (form, setForm, lang, formId) => {
    const fields = form.translations[lang] || emptyLangFields();

    const patch = (partial) =>
      setForm((prev) => ({
        ...prev,
        translations: {
          ...prev.translations,
          [lang]: { ...prev.translations[lang], ...partial },
        },
      }));

    const setArray = (key, updater) =>
      setForm((prev) => {
        const current = prev.translations[lang][key] || [];
        const next = updater([...current]);
        return {
          ...prev,
          translations: {
            ...prev.translations,
            [lang]: { ...prev.translations[lang], [key]: next },
          },
        };
      });

    const cols = fields.dimensionsTableColumns || ACCESSORY_DEFAULT_COLUMNS;
    const dir = lang === "ar" || lang === "ku" ? "rtl" : "ltr";

    return (
      <div className="grid gap-4" dir={dir}>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-sm text-slate-700">
            Name ({lang.toUpperCase()})
            <input
              type="text"
              value={fields.name}
              onChange={(e) => patch({ name: e.target.value })}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
            />
          </label>
          <label className="text-sm text-slate-700">
            Hero Tag ({lang.toUpperCase()})
            <input
              type="text"
              value={fields.heroTag}
              onChange={(e) => patch({ heroTag: e.target.value })}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
            />
          </label>
          <label className="text-sm text-slate-700 sm:col-span-2">
            Hero Title ({lang.toUpperCase()})
            <input
              type="text"
              value={fields.heroTitle}
              onChange={(e) => patch({ heroTitle: e.target.value })}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
            />
          </label>
          <label className="text-sm text-slate-700 sm:col-span-2">
            Hero Description ({lang.toUpperCase()})
            <textarea
              value={fields.heroDescription}
              onChange={(e) => patch({ heroDescription: e.target.value })}
              className="mt-1 w-full min-h-[80px] rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
            />
          </label>
          <label className="text-sm text-slate-700 sm:col-span-2">
            Hero Description More ({lang.toUpperCase()})
            <textarea
              value={fields.heroDescriptionMore}
              onChange={(e) => patch({ heroDescriptionMore: e.target.value })}
              className="mt-1 w-full min-h-[70px] rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
            />
          </label>
        </div>

        <div className="rounded-xl border border-slate-200 p-3">
          <p className="text-sm font-semibold text-[#011337] mb-2">Hero Bullets ({lang.toUpperCase()})</p>
          {fields.heroBullets.map((row, idx) => (
            <div key={`hb-${lang}-${idx}`} className="flex gap-2 mb-2">
              <input
                type="text"
                value={row?.text || ""}
                onChange={(e) =>
                  setArray("heroBullets", (next) => {
                    next[idx] = { text: e.target.value };
                    return next;
                  })
                }
                className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={() =>
                  setArray("heroBullets", (next) => {
                    const out = next.filter((_, i) => i !== idx);
                    return out.length ? out : [{ text: "" }];
                  })
                }
                className="px-3 rounded border border-rose-200 text-rose-700"
              >
                -
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setArray("heroBullets", (next) => [...next, { text: "" }])}
            className="text-xs px-3 py-1.5 rounded border border-slate-300"
          >
            Add bullet
          </button>
        </div>

        <div className="rounded-xl border border-slate-200 p-3">
          <p className="text-sm font-semibold text-[#011337] mb-2">Standards ({lang.toUpperCase()})</p>
          {fields.standards.map((row, idx) => (
            <div key={`st-${lang}-${idx}`} className="flex gap-2 mb-2">
              <input
                type="text"
                value={row?.text || ""}
                onChange={(e) =>
                  setArray("standards", (next) => {
                    next[idx] = { text: e.target.value };
                    return next;
                  })
                }
                className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={() =>
                  setArray("standards", (next) => {
                    const out = next.filter((_, i) => i !== idx);
                    return out.length ? out : [{ text: "" }];
                  })
                }
                className="px-3 rounded border border-rose-200 text-rose-700"
              >
                -
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setArray("standards", (next) => [...next, { text: "" }])}
            className="text-xs px-3 py-1.5 rounded border border-slate-300"
          >
            Add standard
          </button>
        </div>

        <div className="rounded-xl border border-slate-200 p-3">
          <p className="text-sm font-semibold text-[#011337] mb-2">Material Properties ({lang.toUpperCase()})</p>
          {renderPairRows(fields.materialProperties, (next) =>
            setArray("materialProperties", () => next)
          )}
          <button
            type="button"
            onClick={() => setArray("materialProperties", (next) => [...next, { label: "", value: "" }])}
            className="text-xs px-3 py-1.5 rounded border border-slate-300"
          >
            Add property
          </button>
        </div>

        <div className="rounded-xl border border-slate-200 p-3">
          <p className="text-sm font-semibold text-[#011337] mb-2">Section Dimensions ({lang.toUpperCase()})</p>
          {renderPairRows(fields.sectionDimensions, (next) =>
            setArray("sectionDimensions", () => next)
          )}
          <button
            type="button"
            onClick={() => setArray("sectionDimensions", (next) => [...next, { label: "", value: "" }])}
            className="text-xs px-3 py-1.5 rounded border border-slate-300"
          >
            Add section dimension
          </button>
        </div>

        <div className="rounded-xl border border-slate-200 p-3">
          <p className="text-sm font-semibold text-[#011337] mb-3">Dimensions Table ({lang.toUpperCase()})</p>

          <div className="dashboard-table-wrap dashboard-h-scroll-light overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="w-full border-collapse text-sm" dir="ltr">
              <thead>
                <tr className="bg-slate-50">
                  {cols.map((col, colIdx) => (
                    <th
                      key={col.key}
                      className="border-b border-r border-slate-200 px-2 py-1.5 text-left font-medium text-slate-700 whitespace-nowrap last:border-r-0"
                    >
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          disabled={colIdx === 0}
                          onClick={() =>
                            setArray("dimensionsTableColumns", (next) => {
                              [next[colIdx - 1], next[colIdx]] = [next[colIdx], next[colIdx - 1]];
                              return next;
                            })
                          }
                          className="text-slate-400 hover:text-blue-600 disabled:opacity-20 disabled:cursor-not-allowed transition-colors leading-none"
                          title="Move left"
                        >
                          ←
                        </button>
                        <span className="flex-1 text-center">{col.label}</span>
                        <button
                          type="button"
                          disabled={colIdx === cols.length - 1}
                          onClick={() =>
                            setArray("dimensionsTableColumns", (next) => {
                              [next[colIdx], next[colIdx + 1]] = [next[colIdx + 1], next[colIdx]];
                              return next;
                            })
                          }
                          className="text-slate-400 hover:text-blue-600 disabled:opacity-20 disabled:cursor-not-allowed transition-colors leading-none"
                          title="Move right"
                        >
                          →
                        </button>
                        <button
                          type="button"
                          disabled={cols.length <= 1}
                          onClick={() => {
                            if (!window.confirm(`Delete column "${col.label}"? Its values will be removed from every row.`)) return;
                            setForm((prev) => {
                              const t = prev.translations[lang];
                              const current = t.dimensionsTableColumns || ACCESSORY_DEFAULT_COLUMNS;
                              if (current.length <= 1) return prev;
                              const nextCols = current.filter((_, i) => i !== colIdx);
                              const nextRows = (t.dimensionsTableRows || []).map((r) => {
                                const { [col.key]: _removed, ...rest } = r?.values || {};
                                return { ...r, values: rest };
                              });
                              return {
                                ...prev,
                                translations: {
                                  ...prev.translations,
                                  [lang]: { ...t, dimensionsTableColumns: nextCols, dimensionsTableRows: nextRows },
                                },
                              };
                            });
                          }}
                          className="ml-0.5 text-slate-300 hover:text-rose-500 disabled:opacity-20 disabled:cursor-not-allowed transition-colors leading-none"
                          title="Delete column"
                        >
                          ✕
                        </button>
                      </div>
                    </th>
                  ))}
                  <th className="border-b border-slate-200 px-2 py-1.5 w-8 bg-slate-50" />
                </tr>
              </thead>
              <tbody>
                {(fields.dimensionsTableRows || []).map((row, rowIdx) => (
                  <tr key={rowIdx} className="group hover:bg-slate-50/50">
                    {cols.map((col) => (
                      <td key={col.key} className="border-b border-r border-slate-200 px-1 py-0.5 last:border-r-0">
                        <input
                          type="text"
                          value={row?.values?.[col.key] || ""}
                          onChange={(e) =>
                            setArray("dimensionsTableRows", (next) => {
                              next[rowIdx] = {
                                ...next[rowIdx],
                                values: { ...(next[rowIdx]?.values || {}), [col.key]: e.target.value },
                              };
                              return next;
                            })
                          }
                          placeholder="—"
                          className="w-full min-w-[60px] bg-transparent px-1 py-1 text-sm outline-none focus:bg-white focus:ring-1 focus:ring-blue-300 rounded"
                        />
                      </td>
                    ))}
                    <td className="border-b border-slate-200 px-1 py-0.5 text-center">
                      <button
                        type="button"
                        onClick={() =>
                          setArray("dimensionsTableRows", (next) => {
                            const out = next.filter((_, i) => i !== rowIdx);
                            return out.length ? out : [makeAccessoryEmptyRow(cols)];
                          })
                        }
                        className="text-slate-300 hover:text-rose-500 transition-colors text-base leading-none"
                        title="Remove row"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setArray("dimensionsTableRows", (next) => [...next, makeAccessoryEmptyRow(cols)])}
              className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50"
            >
              + Add row
            </button>
            <button
              type="button"
              onClick={() => setAddColumnFor({ formId, lang })}
              className="text-xs px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50"
            >
              + Add column
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderLangTabs = (activeLang, setActiveLang, form, keyPrefix) => (
    <div className="flex flex-wrap items-center gap-2">
      {LANGS.map((lang) => {
        const hasContent = langHasContent(form.translations?.[lang]);
        return (
          <button
            key={`${keyPrefix}-${lang}`}
            type="button"
            onClick={() => setActiveLang(lang)}
            className={activeLang === lang ? langTabActive : langTabIdle}
          >
            {LANG_LABELS[lang]}
            {hasContent ? " ●" : ""}
          </button>
        );
      })}
    </div>
  );

  return (
    <section className="space-y-4" ref={rootRef} onKeyDownCapture={stopSpaceScrollInsideEditor}>
      <div className="flex justify-end">
        <DashboardReloadButton onClick={loadItems} loading={loading} label="Reload list" />
      </div>

      <AdminCollapsibleSection
        title="Add new accessory"
        description="Fill in every language (English, Arabic, Kurdish). All languages are saved together."
        badge="Create"
        footer={
          <button type="button" disabled={creating} onClick={handleCreate} className={btnAccent}>
            {creating ? "Saving..." : "Save accessory (all languages)"}
          </button>
        }
      >
        <div className="grid gap-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-sm text-slate-700">
              Accessory Key (shared across languages)
              <input
                type="text"
                value={createForm.accessoryKey}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, accessoryKey: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
              />
            </label>
            <label className="text-sm text-slate-700">
              Section diagram URL (shared)
              <input
                type="text"
                value={createForm.diagramUrl}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, diagramUrl: e.target.value }))}
                placeholder="/uploads/... or https://..."
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
              />
            </label>
            <label className="inline-flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={Boolean(createForm.isActive)}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, isActive: e.target.checked }))}
              />
              <span>
                <span className="font-medium">Published on website</span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  Uncheck to hide this product from the public site (all languages).
                </span>
              </span>
            </label>
            <label className="inline-flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={Boolean(createForm.isCrossSection)}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, isCrossSection: e.target.checked }))}
              />
              <span>Cross-section layout (shared across languages)</span>
            </label>
            <label className="text-sm text-slate-700 sm:col-span-2">
              Product image file (shared across languages)
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setCreateImageFile(e.target.files?.[0] || null)}
                className="mt-1 block w-full text-sm"
              />
              <span className="mt-1 block text-xs text-slate-500">
                Optional. Uploads automatically after the accessory is saved.
              </span>
            </label>
          </div>

          {renderLangTabs(activeCreateLang, setActiveCreateLang, createForm, "create")}
          {renderLangContent(createForm, setCreateForm, activeCreateLang, "create")}
        </div>
      </AdminCollapsibleSection>

      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
          <h3 className="text-lg font-semibold text-[#011337]">Your accessories</h3>
          {!loading ? (
            <span className="text-sm text-slate-500">
              {groupedItems.length} item{groupedItems.length !== 1 ? "s" : ""}
            </span>
          ) : null}
        </div>
        {loading ? (
          <p className="text-sm text-slate-500 py-8 text-center rounded-xl border border-slate-200 bg-white">
            Loading accessories...
          </p>
        ) : groupedItems.length ? (
          <div className="grid gap-3">
            {groupedItems.map((group) => {
              const missing = LANGS.filter((lang) => !group.languages.includes(lang));
              return (
                <AdminProductListCard
                  key={group.key}
                  id={group.rep?.id}
                  title={group.rep?.name || group.rep?.heroTitle || group.rep?.accessoryKey}
                  meta={[
                    group.rep?.accessoryKey ? `Key: ${group.rep.accessoryKey}` : null,
                    `Languages: ${group.languages.map((l) => l.toUpperCase()).join(", ") || "—"}`,
                    missing.length ? `Missing: ${missing.map((l) => l.toUpperCase()).join(", ")}` : null,
                  ].filter(Boolean)}
                  status={group.rows.every((r) => r?.isActive !== false)}
                  imageUrl={group.rep?.imageUrl}
                  isEditing={editingKey === group.key}
                  onEdit={() => startEdit(group)}
                  onDelete={() => handleDelete(group)}
                />
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-slate-500 py-8 text-center rounded-xl border border-dashed border-slate-300 bg-slate-50">
            No accessories found. Use &quot;Add new accessory&quot; above.
          </p>
        )}
      </div>

      <AdminEditorModal
        open={Boolean(editingKey)}
        title={`Edit accessory: ${editForm.accessoryKey || editingKey || ""}`}
        subtitle="Edit every language, then save. Changes apply to the public product page."
        onClose={closeEdit}
        footer={
          <>
            <button type="button" onClick={closeEdit} className={btnSecondary}>
              Cancel
            </button>
            <button type="button" onClick={saveEdit} disabled={saving} className={btnPrimary}>
              {saving ? "Saving..." : "Save changes"}
            </button>
          </>
        }
      >
        <div className="grid gap-4">
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <p className="text-sm font-semibold text-slate-900 mb-3">Product image (shared across languages)</p>
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              {editForm.imageUrl ? (
                <img
                  src={resolveMediaUrl(editForm.imageUrl)}
                  alt=""
                  className="w-full sm:w-40 h-28 object-cover rounded-lg border border-slate-200 bg-white"
                />
              ) : (
                <div className="w-full sm:w-40 h-28 rounded-lg border border-dashed border-slate-300 bg-white flex items-center justify-center text-xs text-slate-400">
                  No image
                </div>
              )}
              <div className="flex-1 space-y-2 min-w-0">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setEditImageFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm"
                />
                {editImageFile?.name ? (
                  <p className="text-xs text-slate-500">Selected: {editImageFile.name} — uploads when you save changes.</p>
                ) : (
                  <p className="text-xs text-slate-500">Choose a file to replace the current image.</p>
                )}
                {editForm.imageUrl ? (
                  <button
                    type="button"
                    className={btnDanger}
                    onClick={async () => {
                      if (!editRepId) return;
                      try {
                        await removeAdminAccessoryImage({ id: editRepId });
                        setEditForm((prev) => ({ ...prev, imageUrl: "" }));
                        setEditImageFile(null);
                        await refreshAccessoryKey(editForm.accessoryKey);
                        notify({ type: "success", title: "Image removed" });
                      } catch (error) {
                        notify({
                          type: "error",
                          title: "Remove failed",
                          message: error?.response?.data?.message || error?.message || "",
                        });
                      }
                    }}
                  >
                    Remove image
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-sm text-slate-700">
              Accessory Key
              <input
                type="text"
                value={editForm.accessoryKey}
                readOnly
                className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm bg-slate-100 text-slate-500"
              />
              <span className="mt-1 block text-xs text-slate-400">Shared and cannot be changed here.</span>
            </label>
            <label className="text-sm text-slate-700">
              Section diagram URL (shared)
              <input
                type="text"
                value={editForm.diagramUrl}
                onChange={(e) => setEditForm((prev) => ({ ...prev, diagramUrl: e.target.value }))}
                placeholder="/uploads/... or https://..."
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
              />
            </label>
            <label className="inline-flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={Boolean(editForm.isActive)}
                onChange={(e) => setEditForm((prev) => ({ ...prev, isActive: e.target.checked }))}
              />
              <span>
                <span className="font-medium">Published on website</span>
                <span className="block text-xs text-slate-500 mt-0.5">
                  Uncheck to hide this product from the public site (all languages).
                </span>
              </span>
            </label>
            <label className="inline-flex items-start gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={Boolean(editForm.isCrossSection)}
                onChange={(e) => setEditForm((prev) => ({ ...prev, isCrossSection: e.target.checked }))}
              />
              <span>Cross-section layout (shared across languages)</span>
            </label>
          </div>

          {renderLangTabs(activeEditLang, setActiveEditLang, editForm, "edit")}
          {renderLangContent(editForm, setEditForm, activeEditLang, "edit")}

          <AdminProductPdfsPanel
            variant="accessory"
            productId={editRepId}
            productSlug={String(editForm.accessoryKey ?? "").trim()}
            pdfDocuments={items.find((x) => Number(x.id) === Number(editRepId))?.pdfDocuments}
            onRefresh={() => refreshAccessoryKey(editForm.accessoryKey)}
          />
        </div>
      </AdminEditorModal>

      <AdminAddColumnModal
        open={Boolean(addColumnFor)}
        onClose={() => setAddColumnFor(null)}
        onSubmit={handleAddColumn}
      />
    </section>
  );
};

export default MetalsManager;
